import type {
  OTPAuthService,
  OTPLoginRequest,
  OTPRegisterRequest,
  OTPRequestResponse,
  OTPVerifyResponse,
  PhoneLookupResponse,
} from '@/core/infrastructure/services/OTPAuthService';
import type { IUserRepository } from '@/core/domain/repositories/IUserRepository';
import { User, UserStatus } from '@/core/domain/entities/User';

export interface OTPAuthenticationResult {
  user: User;
  authTokens: {
    accessToken: string;
    refreshToken: string;
  };
  registrationStatus: string;
}

export class OTPAuthenticationUseCase {
  constructor(
    private readonly otpAuthService: OTPAuthService,
    private readonly userRepository: IUserRepository
  ) {}

  async lookupPhone(phoneNumber: string): Promise<PhoneLookupResponse> {
    // Validate phone number format first
    const validation = this.otpAuthService.validatePhoneNumber(phoneNumber);
    if (!validation.isValid) {
      throw new Error(validation.error || 'Invalid phone number format');
    }

    return await this.otpAuthService.lookupPhone({
      msisdn: validation.formattedNumber,
    });
  }

  async requestOTP(
    phoneNumber: string,
    purpose: 'SIGNUP' | 'LOGIN'
  ): Promise<OTPRequestResponse> {
    // Validate phone number format first
    const validation = this.otpAuthService.validatePhoneNumber(phoneNumber);
    if (!validation.isValid) {
      throw new Error(validation.error || 'Invalid phone number format');
    }

    // Check rate limiting
    const rateLimitCheck = this.otpAuthService.canRequestOTP(
      validation.formattedNumber
    );
    if (!rateLimitCheck.canRequest) {
      throw new Error(
        `Please wait ${rateLimitCheck.waitTime} seconds before requesting another OTP`
      );
    }

    const result = await this.otpAuthService.requestOTP({
      msisdn: validation.formattedNumber,
      purpose,
    });

    // Record the OTP request for rate limiting
    this.otpAuthService.recordOTPRequest(validation.formattedNumber);

    return result;
  }

  async registerWithOTP(
    phoneNumber: string,
    otpCode: string,
    userData: {
      first_name: string;
      last_name: string;
      email?: string;
      marketing_consent?: boolean;
    }
  ): Promise<OTPAuthenticationResult> {
    // Validate phone number format
    const validation = this.otpAuthService.validatePhoneNumber(phoneNumber);
    if (!validation.isValid) {
      throw new Error(validation.error || 'Invalid phone number format');
    }

    // Validate OTP format
    if (!this.otpAuthService.isValidOTP(otpCode)) {
      throw new Error('Invalid OTP format. Please enter a 6-digit code.');
    }

    const request: OTPRegisterRequest = {
      msisdn: validation.formattedNumber,
      otp_code: otpCode,
      first_name: userData.first_name,
      last_name: userData.last_name,
      email: userData.email,
      marketing_consent: userData.marketing_consent,
      device_info: this.otpAuthService.generateDeviceInfo(),
    };

    const authResult = await this.otpAuthService.registerWithOTP(request);

    // Create user in our database
    const user = await this.userRepository.create({
      authId: '',
      company: '',
      dateOfBirth: new Date(),
      digitalAddress: '',
      gender: '',
      maritalStatus: '',
      occupation: '',
      salary: '',
      id: authResult.user.id,
      name: `${authResult.user.first_name} ${authResult.user.last_name}`,
      email: authResult.user.email,
      phoneNumber: validation.formattedNumber,
      status: UserStatus.ACTIVE,
    });

    return {
      user,
      authTokens: {
        accessToken: authResult.access_token,
        refreshToken: authResult.refresh_token,
      },
      registrationStatus: authResult.registration_status,
    };
  }

  async loginWithOTP(
    phoneNumber: string,
    otpCode: string
  ): Promise<OTPAuthenticationResult> {
    // Validate phone number format
    const validation = this.otpAuthService.validatePhoneNumber(phoneNumber);
    if (!validation.isValid) {
      throw new Error(validation.error || 'Invalid phone number format');
    }

    // Validate OTP format
    if (!this.otpAuthService.isValidOTP(otpCode)) {
      throw new Error('Invalid OTP format. Please enter a 6-digit code.');
    }

    const request: OTPLoginRequest = {
      msisdn: validation.formattedNumber,
      otp_code: otpCode,
      device_info: this.otpAuthService.generateDeviceInfo(),
    };

    const authResult = await this.otpAuthService.loginWithOTP(request);

    return {
      user: authResult.user as any,
      authTokens: {
        accessToken: authResult.access_token,
        refreshToken: authResult.refresh_token,
      },
      registrationStatus: authResult.registration_status,
    };
  }

  async validateOTP(
    phoneNumber: string,
    otpCode: string
  ): Promise<OTPVerifyResponse> {
    return this.otpAuthService.validateOTP(phoneNumber, otpCode);
  }

  validatePhoneNumber(phoneNumber: string): {
    isValid: boolean;
    formattedNumber: string;
    error?: string;
  } {
    return this.otpAuthService.validatePhoneNumber(phoneNumber);
  }

  formatPhoneForDisplay(phoneNumber: string): string {
    return this.otpAuthService.formatPhoneForDisplay(phoneNumber);
  }

  formatOTPInput(input: string): string {
    return this.otpAuthService.formatOTPInput(input);
  }

  isValidOTP(phoneNumber: string, otp: string): boolean {
    return this.otpAuthService.isValidOTP(otp);
  }

  canRequestOTP(phoneNumber: string): {
    canRequest: boolean;
    waitTime?: number;
  } {
    return this.otpAuthService.canRequestOTP(phoneNumber);
  }

  generateDeviceInfo(): any {
    return this.otpAuthService.generateDeviceInfo();
  }

  async getCurrentUser() {
    return {} as any;
  }
}
